import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { User } from '../../services/authService';
import { 
  goldPricesService, 
  GoldPrice, 
  GoldPriceFilters 
} from '../../services/goldPricesService';
import './GoldPrices.css';

interface GoldPricesProps {
  user: User | null;
  onClose: () => void;
  onToggleSidebar: () => void;
}

type TabMode = 'table' | 'chart';

const GoldPrices: React.FC<GoldPricesProps> = ({ user, onToggleSidebar }) => {
  const [activeTab, setActiveTab] = useState<TabMode>('table');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Data states
  const [prices, setPrices] = useState<GoldPrice[]>([]);
  const [regions, setRegions] = useState<string[]>([]);
  const [goldTypes, setGoldTypes] = useState<string[]>([]);
  const [total, setTotal] = useState(0);
  
  // Filter states - Set default date range to last 90 days
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedRegion, setSelectedRegion] = useState<string>('Lịch sử giá vàng TPHCM');
  const [selectedGoldType, setSelectedGoldType] = useState<string>('PNJ');
  const [startDate, setStartDate] = useState<string>(() => {
    const date = new Date();
    date.setDate(date.getDate() - 90);
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState<string>(() => {
    return new Date().toISOString().split('T')[0];
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(100);
  
  // Sorting states
  const [sortColumn, setSortColumn] = useState<keyof GoldPrice | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Extraction states
  const [extracting, setExtracting] = useState(false);
  const [extractionResult, setExtractionResult] = useState<{
    success: boolean;
    message?: string;
    extracted?: number;
    failed?: number;
    missing_dates?: string[];
    failed_dates?: string[];
  } | null>(null);

  useEffect(() => {
    loadRegions();
    loadGoldTypes();
  }, []);

  useEffect(() => {
    loadPrices();
  }, [selectedRegion, selectedGoldType, startDate, endDate, currentPage]);

  const loadPrices = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: GoldPriceFilters = {
        region: selectedRegion || undefined,
        gold_type: selectedGoldType || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit: itemsPerPage,
        offset: (currentPage - 1) * itemsPerPage
      };

      const response = await goldPricesService.getGoldPrices(filters);
      
      if (response.success && response.prices) {
        setPrices(response.prices);
        setTotal(response.total || 0);
      } else {
        setError('Failed to load gold prices');
      }
    } catch (err) {
      console.error('Error loading gold prices:', err);
      setError('Failed to load gold prices');
    } finally {
      setLoading(false);
    }
  };

  const loadRegions = async () => {
    try {
      const response = await goldPricesService.getRegions();
      if (response.success && response.regions) {
        setRegions(response.regions);
      }
    } catch (err) {
      console.error('Error loading regions:', err);
    }
  };

  const loadGoldTypes = async () => {
    try {
      const response = await goldPricesService.getGoldTypes();
      if (response.success && response.types) {
        setGoldTypes(response.types);
      }
    } catch (err) {
      console.error('Error loading gold types:', err);
    }
  };

  const handleSort = (column: keyof GoldPrice) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedPrices = useMemo(() => {
    if (!sortColumn) return prices;
    
    return [...prices].sort((a, b) => {
      const aVal = a[sortColumn];
      const bVal = b[sortColumn];
      
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' 
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      
      if (typeof aVal === 'number' && typeof bVal === 'number') {
        return sortDirection === 'asc' 
          ? aVal - bVal
          : bVal - aVal;
      }
      
      return 0;
    });
  }, [prices, sortColumn, sortDirection]);

  // Load chart data separately with chart-specific filters
  const [chartPrices, setChartPrices] = useState<GoldPrice[]>([]);
  
  useEffect(() => {
    if (activeTab === 'chart') {
      loadChartData();
    }
  }, [selectedRegion, selectedGoldType, startDate, endDate, activeTab]);

  const loadChartData = async () => {
    try {
      const filters: GoldPriceFilters = {
        region: selectedRegion || undefined,
        gold_type: selectedGoldType || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        limit: 10000 // Load more data for chart
      };

      const response = await goldPricesService.getGoldPrices(filters);
      
      if (response.success && response.prices) {
        setChartPrices(response.prices);
      }
    } catch (err) {
      console.error('Error loading chart data:', err);
    }
  };

  const chartData = useMemo(() => {
    if (!chartPrices || chartPrices.length === 0) {
      return [];
    }

    // Group by date and calculate average prices if multiple records per date
    const dateMap = new Map<string, { buy: number[]; sell: number[]; dateStr: string }>();
    
    chartPrices.forEach(price => {
      // Normalize date to YYYY-MM-DD format for grouping (handles both date strings and timestamps)
      const dateObj = new Date(price.date);
      const dateKey = dateObj.toISOString().split('T')[0]; // Extract just the date part
      
      if (!dateMap.has(dateKey)) {
        dateMap.set(dateKey, { 
          buy: [], 
          sell: [],
          dateStr: goldPricesService.formatDate(price.date)
        });
      }
      const entry = dateMap.get(dateKey)!;
      
      // Only push valid numeric prices
      if (price.buy_price != null && !isNaN(price.buy_price) && isFinite(price.buy_price)) {
        entry.buy.push(price.buy_price);
      }
      if (price.sell_price != null && !isNaN(price.sell_price) && isFinite(price.sell_price)) {
        entry.sell.push(price.sell_price);
      }
    });
    
    const data = Array.from(dateMap.entries())
      .map(([date, values]) => {
        const avgBuy = values.buy.length > 0 
          ? values.buy.reduce((a, b) => a + b, 0) / values.buy.length 
          : 0;
        const avgSell = values.sell.length > 0
          ? values.sell.reduce((a, b) => a + b, 0) / values.sell.length
          : 0;
        
        return {
          date: values.dateStr,
          dateKey: date,
          buyPrice: Math.round(avgBuy * 100) / 100,
          sellPrice: Math.round(avgSell * 100) / 100
        };
      })
      .filter(item => item.buyPrice > 0 || item.sellPrice > 0) // Filter out entries with no valid prices
      .sort((a, b) => {
        const dateA = new Date(a.dateKey);
        const dateB = new Date(b.dateKey);
        return dateA.getTime() - dateB.getTime();
      });
    
    return data;
  }, [chartPrices]);

  const resetFilters = () => {
    setSelectedRegion('Lịch sử giá vàng TPHCM');
    setSelectedGoldType('PNJ');
    // Reset to default date range (last 90 days)
    const today = new Date();
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(today.getDate() - 90);
    setStartDate(ninetyDaysAgo.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
    setCurrentPage(1);
  };

  const handleExtractMissing = async () => {
    try {
      setExtracting(true);
      setExtractionResult(null);
      setError(null);
      
      const result = await goldPricesService.extractMissingPrices();
      
      setExtractionResult(result);
      
      // Reload prices after successful extraction
      if (result.success && result.extracted && result.extracted > 0) {
        await loadPrices();
        if (activeTab === 'chart') {
          await loadChartData();
        }
      }
    } catch (err) {
      console.error('Error extracting missing prices:', err);
      setError('Failed to extract missing prices');
      setExtractionResult({
        success: false,
        message: 'Failed to extract missing prices'
      });
    } finally {
      setExtracting(false);
    }
  };

  const totalPages = Math.ceil(total / itemsPerPage);

  const renderFilters = () => (
    <div className="gp-filters-section">
      <button 
        className="gp-filters-toggle" 
        onClick={() => setFiltersExpanded(!filtersExpanded)}
      >
        <span className="gp-toggle-text">/filters</span>
        <span className="gp-toggle-arrow">{filtersExpanded ? '▼' : '▶'}</span>
      </button>
      
      <div className={`gp-filters-content ${filtersExpanded ? 'expanded' : 'collapsed'}`}>
        <div className="gp-filters-grid">
          <div className="gp-filter-item">
            <label className="gp-filter-label">Region</label>
            <select
              value={selectedRegion}
              onChange={(e) => {
                setSelectedRegion(e.target.value);
                setCurrentPage(1);
              }}
              className="gp-filter-select"
            >
              <option value="">All Regions</option>
              {regions.map(region => (
                <option key={region} value={region}>{region}</option>
              ))}
            </select>
          </div>

          <div className="gp-filter-item">
            <label className="gp-filter-label">Gold Type</label>
            <select
              value={selectedGoldType}
              onChange={(e) => {
                setSelectedGoldType(e.target.value);
                setCurrentPage(1);
              }}
              className="gp-filter-select"
            >
              <option value="">All Types</option>
              {goldTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div className="gp-filter-item">
            <label className="gp-filter-label">Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setStartDate(e.target.value);
                setCurrentPage(1);
              }}
              className="gp-filter-input"
            />
          </div>

          <div className="gp-filter-item">
            <label className="gp-filter-label">End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setEndDate(e.target.value);
                setCurrentPage(1);
              }}
              className="gp-filter-input"
            />
          </div>
        </div>

        <div className="gp-filters-actions">
          <button 
            onClick={handleExtractMissing} 
            className="gp-extract-btn"
            disabled={extracting}
          >
            {extracting ? 'Extracting...' : 'Extract Missing Prices'}
          </button>
          <button onClick={resetFilters} className="gp-reset-btn">
            Reset Filters
          </button>
        </div>
      </div>
    </div>
  );

  const renderTableView = () => (
    <div className="gp-table-container">
      <table className="gp-table">
        <thead>
          <tr>
            <th 
              className="gp-table-header gp-sortable"
              onClick={() => handleSort('date')}
            >
              Date {sortColumn === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              className="gp-table-header gp-sortable"
              onClick={() => handleSort('region')}
            >
              Region {sortColumn === 'region' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              className="gp-table-header gp-sortable"
              onClick={() => handleSort('gold_type')}
            >
              Gold Type {sortColumn === 'gold_type' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              className="gp-table-header gp-sortable"
              onClick={() => handleSort('buy_price')}
            >
              Buy Price {sortColumn === 'buy_price' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              className="gp-table-header gp-sortable"
              onClick={() => handleSort('sell_price')}
            >
              Sell Price {sortColumn === 'sell_price' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
            <th 
              className="gp-table-header gp-sortable"
              onClick={() => handleSort('update_time')}
            >
              Update Time {sortColumn === 'update_time' && (sortDirection === 'asc' ? '↑' : '↓')}
            </th>
          </tr>
        </thead>
        <tbody>
          {sortedPrices.length === 0 ? (
            <tr>
              <td colSpan={6} className="gp-table-empty">
                No data available
              </td>
            </tr>
          ) : (
            sortedPrices.map((price) => (
              <tr key={price.id} className="gp-table-row">
                <td>{goldPricesService.formatDate(price.date)}</td>
                <td>{price.region}</td>
                <td>{price.gold_type}</td>
                <td>{goldPricesService.formatCurrency(price.buy_price)}</td>
                <td>{goldPricesService.formatCurrency(price.sell_price)}</td>
                <td>{goldPricesService.formatDateTime(price.update_time)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>

      {totalPages > 1 && (
        <div className="gp-pagination">
          <button
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className="gp-pagination-btn"
          >
            Previous
          </button>
          <span className="gp-pagination-info">
            Page {currentPage} of {totalPages} ({total} total records)
          </span>
          <button
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className="gp-pagination-btn"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );

  const renderChartView = () => {
    if (loading && chartPrices.length === 0) {
      return (
        <div className="gp-chart-container">
          <div className="gp-chart-empty">
            Loading chart data...
          </div>
        </div>
      );
    }

    if (chartData.length === 0) {
      return (
        <div className="gp-chart-container">
          <div className="gp-chart-empty">
            No data available for the selected filters. Try adjusting the date range or region.
          </div>
        </div>
      );
    }

    return (
      <div className="gp-chart-view">
        <div className="gp-chart-container">
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
              <XAxis 
                dataKey="date" 
                stroke="#666666"
                tick={{ fill: '#666666', fontSize: 12 }}
                angle={-45}
                textAnchor="end"
                height={80}
              />
              <YAxis 
                stroke="#666666"
                tick={{ fill: '#666666', fontSize: 12 }}
                tickFormatter={(value) => {
                  return new Intl.NumberFormat('vi-VN', {
                    notation: 'compact',
                    maximumFractionDigits: 0
                  }).format(value);
                }}
              />
              <Tooltip 
                formatter={(value: number) => goldPricesService.formatCurrency(value)}
                labelStyle={{ color: '#333333' }}
                contentStyle={{ 
                  backgroundColor: '#ffffff', 
                  border: '1px solid #e0e0e0',
                  borderRadius: '0.25rem'
                }}
              />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="buyPrice" 
                stroke="#28a745" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
                name="Buy Price"
              />
              <Line 
                type="monotone" 
                dataKey="sellPrice" 
                stroke="#dc3545" 
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 5 }}
                name="Sell Price"
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    );
  };

  return (
    <div className="gold-prices">
      <div className="gp-header">
        <button className="gp-sidebar-toggle" onClick={onToggleSidebar}>☰</button>
        <h1 className="gp-title">🥇 Gold Prices</h1>
      </div>

      {error && (
        <div className="gp-error-message">
          <span>{error}</span>
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {extractionResult && (
        <div className={`gp-extraction-result ${extractionResult.success ? 'success' : 'error'}`}>
          <span>
            {extractionResult.message || 
              (extractionResult.success 
                ? `Extracted ${extractionResult.extracted || 0} records${extractionResult.failed && extractionResult.failed > 0 ? ` (${extractionResult.failed} failed)` : ''}`
                : 'Extraction failed')}
          </span>
          <button onClick={() => setExtractionResult(null)}>✕</button>
        </div>
      )}

      {/* Tabs */}
      <div className="gp-tabs">
        <button
          className={`gp-tab ${activeTab === 'table' ? 'active' : ''}`}
          onClick={() => setActiveTab('table')}
        >
          Table
        </button>
        <button
          className={`gp-tab ${activeTab === 'chart' ? 'active' : ''}`}
          onClick={() => setActiveTab('chart')}
        >
          Chart
        </button>
      </div>

      {/* Render filters outside of the tab content to be persistent */}
      {renderFilters()}

      {activeTab === 'table' && (
        <>
          {loading ? (
            <div className="gp-loading-state">
              <div className="gp-loading-spinner"></div>
              <p>Loading gold prices...</p>
            </div>
          ) : (
            <div className="gp-content">
              {renderTableView()}
            </div>
          )}
        </>
      )}

      {activeTab === 'chart' && (
        <div className="gp-content">
          {renderChartView()}
        </div>
      )}
    </div>
  );
};

export default GoldPrices;

